// Customizable Area Start
import { IBlock } from "../../../framework/src/IBlock";
import { BlockComponent } from "../../../framework/src/BlockComponent";
import MessageEnum, {
  getName,
} from "../../../framework/src/Messages/MessageEnum";
import { runEngine } from "../../../framework/src/RunEngine";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometCall, User } from "./ChatController";
import React from "react";
import { CometChatCalls } from "@cometchat/calls-sdk-javascript";
import { CallRingingSound } from "./assets";
import { InfoType } from "../../../components/src/CustomInfoDialog.web";
export const configJSON = require("./config");

const CallListenerID = "UNIQUE_LISTENER_ID";
const CallEventListenerID = "UNIQUE_CALL_LISTENER_ID";
// Customizable Area End

// Customizable Area Start
// Customizable Area End

export interface Props {
  // Customizable Area Start
  isOpen: boolean;
  recipientInfo: User;
  isVideoCall: boolean;
  handleUpdateCallMsg: (call: CometCall) => void;
  handleOpenInfoDialog: (infoType: InfoType) => void;
  handleCloseAudioCallDialog: () => void;
  // Customizable Area End
}

interface S {
  // Customizable Area Start
  isFullScreen: boolean;
  callStatus: string;
  callTimer: number;
  isMuted: boolean;
  isVideoOff: boolean;
  cometCall: CometCall | null;
  isCallStarted: boolean;
  // Customizable Area End
}

interface SS {
  id: string;
  // Customizable Area Start
  // Customizable Area End
}

export default class CallModalController extends BlockComponent<Props, S, SS> {
  // Customizable Area Start
  timer: number | null = null;
  private remoteAudio: HTMLAudioElement | null = null;
  callContainerRef: React.RefObject<HTMLDivElement> = React.createRef();
  // Customizable Area End

  constructor(props: Props) {
    super(props);
    this.receive = this.receive.bind(this);

    this.subScribedMessages = [
      // Customizable Area Start
      getName(MessageEnum.SessionResponseMessage),
      getName(MessageEnum.RestAPIResponceMessage),
      getName(MessageEnum.NavigationPayLoadMessage),
      // Customizable Area End
    ];

    this.state = {
      // Customizable Area Start
      isFullScreen: false,
      callStatus: "",
      callTimer: 0,
      isMuted: false,
      isVideoOff: false,
      cometCall: null,
      isCallStarted: false,
      // Customizable Area End
    };
    runEngine.attachBuildingBlock(this as IBlock, this.subScribedMessages);

    // Customizable Area Start
    // Customizable Area End
  }

  // Customizable Area Start
  async componentDidMount(): Promise<void> {
    this.remoteAudio = new Audio();
    this.handleStartCall();
    this.addCallListeners();
  }

  async componentWillUnmount(): Promise<void> {
    this.resetCall();
  }

  addCallListeners = () => {
    CometChat.addCallListener(
      CallListenerID,
      new CometChat.CallListener({
        onOutgoingCallRejected: (call: CometCall) => {
          this.pauseRingingSound();
          this.props.handleUpdateCallMsg(call);
          this.props.handleCloseAudioCallDialog();
        },
        onOutgoingCallAccepted: this.handleStartCallSession,
        onCallEndedMessageReceived: this.props.handleUpdateCallMsg,
      })
    );
  };

  addOngoingCallListener = () => {
    CometChatCalls.addCallEventListener(
      CallEventListenerID,
      new CometChatCalls.OngoingCallListener({
        onCallEnded: this.resetCall,
      })
    );
  };

  removeOngoingCallListener = () => {
    CometChatCalls.removeCallEventListener(CallEventListenerID);
  };

  removeCallListeners = () => {
    CometChat.removeCallListener(CallListenerID);
    this.removeOngoingCallListener();
  };

  resetCall = () => {
    try {
      this.stopTimer();
      CometChatCalls.endSession();
      CometChat.clearActiveCall();
      this.removeCallListeners();
      this.props.handleCloseAudioCallDialog();
    } catch (err) {}
  };

  handleStartCall = async () => {
    const { recipientInfo, isVideoCall } = this.props;
    if (recipientInfo) {
      const { handleOpenInfoDialog, handleUpdateCallMsg } = this.props;
      try {
        const callType = isVideoCall
          ? CometChat.CALL_TYPE.VIDEO
          : CometChat.CALL_TYPE.AUDIO;
        const receiverType = CometChat.RECEIVER_TYPE.USER;

        if (this.remoteAudio) {
          this.remoteAudio.src = CallRingingSound;
          this.remoteAudio.loop = true;
          this.remoteAudio.play().catch(() => {});
        }

        const call = new CometChat.Call(
          recipientInfo.uid,
          callType,
          receiverType
        );

        const callInstance = ((await CometChat.initiateCall(
          call
        )) as unknown) as CometCall;
        handleUpdateCallMsg(callInstance);
        this.setState({ cometCall: callInstance });
      } catch (err) {
        handleOpenInfoDialog("error");
      }
    }
  };

  handleStartCallSession = async (cometCall: CometCall) => {
    try {
      const { isVideoCall } = this.props;

      const callSettings = new CometChatCalls.CallSettingsBuilder()
        .enableDefaultLayout(false)
        .setIsAudioOnlyCall(!isVideoCall)
        .showVirtualBackgroundSetting(false)
        .showModeButton(false)
        .build();

      const loggedInUser = await CometChat.getLoggedinUser();
      if (loggedInUser) {
        const authToken = loggedInUser.getAuthToken();

        const tokenRes = await CometChatCalls.generateToken(
          cometCall.sessionId,
          authToken
        );
        const callElement = this.callContainerRef.current as HTMLDivElement;
        this.pauseRingingSound();
        await CometChatCalls.startSession(
          tokenRes.token,
          callSettings,
          callElement
        );
        this.addOngoingCallListener();
        this.props.handleUpdateCallMsg(cometCall);
        this.startTimer();
        this.setState({ isCallStarted: true });
      }
    } catch (error) {}
  };

  toggleFullScreen = () => {
    this.setState({ isFullScreen: !this.state.isFullScreen });
  };

  cancelOutgoingCall = async () => {
    try {
      const { cometCall } = this.state;
      if (cometCall) {
        const { handleCloseAudioCallDialog, handleUpdateCallMsg } = this.props;
        const status = CometChat.CALL_STATUS.CANCELLED;

        this.pauseRingingSound();
        const call = await CometChat.rejectCall(cometCall.sessionId, status);
        handleUpdateCallMsg((call as unknown) as CometCall);
        this.resetCall();
        handleCloseAudioCallDialog();
      }
    } catch (error) {}
  };

  endCall = async () => {
    const { cometCall } = this.state;
    if (cometCall) {
      const call = await CometChat.endCall(cometCall.sessionId);
      this.props.handleUpdateCallMsg((call as unknown) as CometCall);
      this.resetCall();
    }
  };

  handleEndCall = () => {
    const { isCallStarted } = this.state;
    if (isCallStarted) {
      this.endCall();
    } else {
      this.cancelOutgoingCall();
    }
  };

  pauseRingingSound = () => {
    try {
      if (this.remoteAudio) {
        this.remoteAudio.pause();
        this.remoteAudio.src = "";
      }
    } catch (err) {}
  };

  stopTimer = () => {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }

    this.setState({ callTimer: 0 });
  };

  startTimer = () => {
    this.timer = window.setInterval(() => {
      this.setState((prevState) => ({
        callTimer: prevState.callTimer + 1,
      }));
    }, 1000);
  };

  toggleVideo = () => {
    const { isVideoOff } = this.state;
    const newVideoState = !isVideoOff;
    CometChatCalls.pauseVideo(newVideoState);
    this.setState({ isVideoOff: newVideoState });
  };

  toggleMute = () => {
    const { isMuted } = this.state;
    const newMutedState = !isMuted;
    CometChatCalls.muteAudio(newMutedState);
    this.setState({ isMuted: newMutedState });
  };
  // Customizable Area End
}
